import { observable } from 'mobx';

import type {
    SubscriptionOrderData,
    SubscriptionOrderListQuery,
    SubscriptionOrderSorting,
} from 'chaika-frontend-types/frontend-cms';

import { SubscriptionOrderApi } from '@api/SubscriptionOrderApi';
import { subscriptionOrderStore } from '../store/SubscriptionOrderStore';
import { patientService } from '@modules/patient/services/patient/patientService';
import { userService } from '@modules/user/services/userService';
import { planService } from '@modules/plan/service/planService';
import { isDefined } from '@utils/isDefined';
import { subscriptionService } from '@patient/services/subscriptionService';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import { getUniqNewIds } from '@utils/getUniqNewIds';

export class SubscriptionOrderService {
    @observable public isLoading = false;
    @observable public isLoaded = false;

    private readonly store = subscriptionOrderStore;

    public async reload(params: SubscriptionOrderListQuery, withPagination?: boolean): Promise<void> {
        this.reset();
        await this.load(params, withPagination);
    }

    public async changeOrder(sorting: SubscriptionOrderSorting): Promise<void> {
        const params = this.store.subscriptionOrdersList.getFilterParams();
        this.reload({ ...params, sorting });
    }

    public async load(params: SubscriptionOrderListQuery, withPagination?: boolean): Promise<void> {
        if (!this.isLoading) {
            this.isLoading = true;

            this.store.subscriptionOrdersList.reset();

            const paramsWithPagination = {
                ...params,
                ...this.store.subscriptionOrdersList.queryParams,
            };

            const { subscriptionOrders, count } = await SubscriptionOrderApi.getList(
                withPagination ? paramsWithPagination : params,
            );

            await this.loadAdditionalData(subscriptionOrders);

            this.store.subscriptionOrdersList.set(subscriptionOrders);
            this.store.subscriptionOrdersList.setFilterParams(params);
            this.store.subscriptionOrdersList.count = count;

            this.isLoading = false;
            this.isLoaded = true;
        }
    }

    public async loadNext(): Promise<void> {
        if (this.store.subscriptionOrdersList.hasNext) {
            const { subscriptionOrders } = await SubscriptionOrderApi.getList({
                ...this.store.subscriptionOrdersList.getFilterParams(),
                ...this.store.subscriptionOrdersList.queryParams,
            });

            await this.loadAdditionalData(subscriptionOrders);
            this.store.subscriptionOrdersList.addNext(subscriptionOrders);
        }
    }

    public async refund(subscriptionOrderId: string): Promise<void> {
        await SubscriptionOrderApi.refund(subscriptionOrderId);
    }

    public async updatePatient(subscriptionOrderId: string, patientId: string): Promise<void> {
        await SubscriptionOrderApi.updatePatient(subscriptionOrderId, patientId);

        this.store.update(subscriptionOrderId, {
            patientId,
        });
    }

    public async activate(subscriptionOrderId: string): Promise<void> {
        await SubscriptionOrderApi.activate(subscriptionOrderId);

        const {
            subscriptionOrders: [subscriptionOrder],
        } = await SubscriptionOrderApi.getList({ id: subscriptionOrderId });

        this.store.update(subscriptionOrderId, {
            ...subscriptionOrder,
        });
    }

    public async createChat(subscriptionOrderId: string): Promise<void> {
        const subscriptionOrder = this.store.getById(subscriptionOrderId);

        if (isDefined(subscriptionOrder) && isDefined(subscriptionOrder.doctorId)) {
            await SubscriptionOrderApi.createChat(subscriptionOrderId, subscriptionOrder.doctorId);
        }
    }

    public reset(): void {
        this.store.set([]);
        this.isLoading = false;
        this.isLoaded = false;
        this.store.subscriptionOrdersList.reset();
    }

    private async loadAdditionalData(subscriptionOrders: SubscriptionOrderData[]): Promise<void> {
        const { patientIds, doctorIds, planIds, subscriptionIds } = subscriptionOrders.reduce(
            (acc, cur) => {
                if (cur.patientId) {
                    acc.patientIds.add(cur.patientId);
                }
                if (cur.doctorId) {
                    acc.doctorIds.add(cur.doctorId);
                }
                if (cur.planId) {
                    acc.planIds.add(cur.planId);
                }
                if (cur.subscriptionId) {
                    acc.subscriptionIds.add(cur.subscriptionId);
                }
                return acc;
            },
            {
                patientIds: new Set<string>(),
                doctorIds: new Set<string>(),
                planIds: new Set<string>(),
                subscriptionIds: new Set<string>(),
            },
        );

        if (subscriptionOrders.length > 0) {
            await Promise.all([
                patientService.loadByIds(toArrayFromIterable(patientIds)),
                userService.loadIfNotExist(toArrayFromIterable(doctorIds)),
                planService.loadByIds(toArrayFromIterable(planIds)),
            ]);
            const newUniqIds = getUniqNewIds(
                this.store.subscriptionAdditionalDataList.ids,
                toArrayFromIterable(subscriptionIds),
            );
            const subscriptionData = await subscriptionService.getListByIds(newUniqIds);

            this.store.subscriptionAdditionalDataList.add(subscriptionData);
        }
    }
}

export const subscriptionOrderService = new SubscriptionOrderService();
